import React, { useEffect, useRef, useState } from 'react'; // Import useEffect, useRef, useState
import { Box, Typography, TextField, Button } from '@mui/material';
import { AddIcon } from 'icons/materialIcons'; // Import centralized icon
import { useCardContext } from 'contexts/CardContext'; // Import the CardContext
import AddContactLinksModal from './links/AddContactLinkModal'; // Import the modal component
import { validateEmail, validatePhoneNumber } from 'utils/validator'; // Import the validateEmail function
import PhoneInputField from 'components/common/PhoneInputField'; // Import PhoneInputField component

export default function CardDetails({ onContinue, isTemporaryCard = true }) { // default to true since this is typically used during signup
  const { cardId, cardState, setCardEmail, setCardPhoneNumber } = useCardContext(); // Access state and functions from CardContext
  const cardEmail = cardState[cardId]?.cardEmail || '';
  const cardPhoneNumber = cardState[cardId]?.cardPhoneNumber || '';
  // Modal state management
  const [openModal, setOpenModal] = useState(false);
  const [emailError, setEmailError] = useState(''); // State for email error message
  const [phoneError, setPhoneError] = useState(''); // State for phone number error message

  // Create a reference for the Email input field
  const emailInputRef = useRef(null);

  useEffect(() => {
    // Focus on the Email input field when the component mounts
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  // Handler for email input change
  const handleCardEmailChange = (event) => {
    const newCardEmail = event.target.value;
    console.log('Card Email Changed:', newCardEmail);

    // Validate email using the external function
    if (validateEmail(newCardEmail)) {
      setEmailError('');
    } else {
      setEmailError('Invalid email address!');
    }

    setCardEmail(cardId, newCardEmail);
  };

  // Handler for phone number input change
  const handleCardPhoneNumberChange = (value) => {
    console.log('Card Phone Number Changed:', value);

    // Only validate if there's a phone number
    if (value && value.trim() !== '') {
      // Validate phone number using the external function
      if (validatePhoneNumber(value)) {
        setPhoneError(''); // Clear error if the number is valid
      } else {
        setPhoneError('Please enter a valid phone number'); // Set error if invalid
      }
    } else {
      setPhoneError(''); // Clear error if the phone number is empty
    }

    setCardPhoneNumber(cardId, value); // Update the card phone number in context
  };

  // Function to open the modal
  const handleAddLinksAndContact = () => {
    setOpenModal(true); // Open modal when the button is clicked
  };

  // Function to close the modal
  const handleCloseModal = () => {
    setOpenModal(false); // Close modal
  };

  // Prevent default form submission behavior on keydown and trigger continue on Enter
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent form submission
      if (!emailError && !phoneError) {
        onContinue(); // Trigger the continue action only if there's no email or phone error
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: 3,
      }}
    >
      <Typography variant="h5" gutterBottom>
        Additional Info
      </Typography>
      <Typography variant="body2" sx={{ marginBottom: 2, textAlign: 'center' }}>
        Add your contact details below. This will help in connecting with you.
      </Typography>

      <TextField
        label="Email"
        type="email"
        inputRef={emailInputRef} // Set the ref for the Email field
        value={cardEmail} // Bind the input value to cardEmail from context
        onChange={handleCardEmailChange} // Handle the change to update cardEmail in context
        variant="outlined"
        fullWidth
        sx={{ marginBottom: 2 }}
        onKeyDown={handleKeyDown} // Handle key down to prevent default
        error={!!emailError} // Show error state if there's an error
        helperText={emailError} // Display error message below the input
        placeholder="your.email@example.com"
      />

      <Box sx={{ width: '100%', marginBottom: 2 }}>
        <Typography
          sx={{
            fontSize: '0.8rem',
            fontWeight: 600,
            color: '#4f4f4f',
            marginBottom: '10px'
          }}
        >
          Phone Number
        </Typography>
        <PhoneInputField
          value={cardPhoneNumber}
          onChange={handleCardPhoneNumberChange}
          error={phoneError}
          country="sg"
          placeholder="Enter phone number"
          containerStyle={{ marginBottom: '10px' }}
        />
      </Box>

      <Button
        fullWidth
        variant="outlined"
        startIcon={<AddIcon />}
        sx={{ marginTop: 1, marginBottom: 2, textTransform: 'none' }}
        onClick={handleAddLinksAndContact}
      >
        Add Links & Contact Info
      </Button>

      <Button
        fullWidth
        variant="contained"
        color="primary"
        sx={{ backgroundColor: 'blue', borderRadius: '4px', textTransform: 'none' }}
        onClick={onContinue}
        disabled={!!emailError || !!phoneError} // Disable button if there's an email or phone error
      >
        Continue
      </Button>

      {/* ✅ CONTEXT-ONLY: Modal Component with updated props */}
      <AddContactLinksModal
        open={openModal}
        handleClose={handleCloseModal}
        isTemporaryCard={isTemporaryCard}  // Pass the flag here - supports context-only approach
      />
    </Box>
  );
}